/**
 * catalogueImportTemplate: how every Lusuku import workbook is laid out.
 * ───────────────────────────────────────────────────────────────────────────
 * The columns, their Title Case labels, hints and formulas come from
 * catalogueImportColumns.js and the vertical's own columns file, the same lists
 * the parser reads headers with, so a template and its parser cannot disagree.
 * This file only lays them out, and it does so identically for every vertical.
 *
 * The layout follows the China Town workbook, the blueprint for every Lusuku
 * product sheet since 26 Sep 2026: a READ ME, a Column Guide, Title Case
 * headers with a one-line hint above each, a Status column, columns that fill
 * themselves in (To Do, Image Source, the section counts), and a Lists sheet of
 * reference values. Verticals supply their own READ ME prose, their own Lists
 * entries and their own example row. Nothing else varies, so nothing else is
 * duplicated.
 *
 * The `xlsx` build used here writes no colours; the blueprint in Drive carries
 * the styled version with its colour key.
 *
 * DELIBERATELY BLANK. The single example row carries the marker string the
 * parser drops, and no worked-example sheet ships at all: sheet picking is by
 * row count, so a filled example could outvote the operator's own rows.
 */
import * as XLSX from "xlsx";
import {
  PRODUCTS_SHEET,
  ROLE,
  ROLE_LABEL,
  SECTIONS_SHEET,
  columnLetter,
} from "./catalogueImportColumns.js";
import { PRODUCT_PHOTO_README_ROWS, STAND_IN_PHOTO_FILE } from "./productPhotoRules.js";

/* Every vertical's READ ME carries the same PHOTOS section and the same note
   about Lusuku's own stand-in cover, so they are written once here. */
export const PHOTO_README_ROWS = PRODUCT_PHOTO_README_ROWS;
export const STAND_IN_NOTE = `No stand-in of your own? Download Lusuku's, ${STAND_IN_PHOTO_FILE}, from the import dialog, upload it with the workbook and type its name above.`;

/** Dropped by the parser. Must match EXAMPLE_ROW_MARKER in catalogueImport.js. */
export const EXAMPLE_NOTE = "Delete this example row before importing";

/* Rows that carry the self-filling formulas. Rows past the last product stay
   blank (the formulas return "" when a row has no Section and no Name), and the
   importer skips blank rows. */
export const PRODUCT_FORMULA_ROWS = 500;
export const SECTION_FORMULA_ROWS = 60;

/* Header on row 2, hints on row 1, data from row 3, on both data sheets. */
export const HINT_ROW = 1;
export const FIRST_DATA_ROW = 3;

export const README_SHEET = "READ ME";
export const GUIDE_SHEET = "Column Guide";
export const LISTS_SHEET = "3. Lists";

const hintFor = (c) => c.hint;
const guideText = (c) => (c.detail ? `${c.hint} ${c.detail}` : c.hint);

/**
 * The Column Guide rows for one vertical: every section column, then every
 * product column, then whatever extra headers that vertical adds at download
 * time (a brand's spec columns).
 */
export const buildGuideRows = (sectionColumns, productColumns, extraRows = []) => {
  const rows = [["Sheet", "Column", "Needed?", "What to enter"]];
  for (const c of sectionColumns) rows.push([SECTIONS_SHEET, c.label, ROLE_LABEL[c.role], guideText(c)]);
  for (const c of productColumns) rows.push([PRODUCTS_SHEET, c.label, ROLE_LABEL[c.role], guideText(c)]);
  for (const r of extraRows) rows.push(r);
  return rows;
};

/**
 * The reference values every vertical's Lists sheet opens with.
 *
 * Status, Availability and Payment Method are read by the same parser code on
 * every vertical, so spelling them out per vertical would be three chances for
 * one list to fall behind the enum it documents.
 */
export const SHARED_LIST_ROWS = (paymentMethods) => [
  ["Reference values. Copy them exactly: spelling is checked, capitalisation is not."],
  [],
  ["Status"],
  ["Active"],
  ["Deactivated"],
  [],
  ["Availability"],
  ["In Stock"],
  ["Out of Stock"],
  [],
  ["Payment Method"],
  ...paymentMethods.map((m) => [m]),
  [],
];

/**
 * The Yes / No block, placed by each vertical rather than baked into the list
 * above, because a vertical with an enum of its own (brand's Order Flow,
 * healthcare's Product Type) reads better with that enum next to the other
 * enums and Yes / No after them.
 */
export const YES_NO_LIST_ROWS = [["Yes / No columns"], ["Yes"], ["No"], []];

export const widthFor = (label, key) =>
  ["description", "notes", "section_description"].includes(key)
    ? { wch: 40 }
    : { wch: Math.min(Math.max(String(label).length + 4, 12), 30) };

/**
 * One data sheet: hints on row 1, Title Case headers on row 2, then `rows`
 * (objects keyed by field key), with every AUTO column carrying its formula
 * down to `formulaRows`.
 */
export const dataSheet = (columns, extraHeaders, rows, formulaFor, formulaRows) => {
  const headers = [...columns.map((c) => c.label), ...extraHeaders.map((h) => h.label)];
  const hints = [...columns.map(hintFor), ...extraHeaders.map((h) => h.hint)];
  const keys = [...columns.map((c) => c.key), ...extraHeaders.map((h) => h.key)];
  const aoa = [hints, headers, ...rows.map((r) => keys.map((k) => (k in r ? r[k] : null)))];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const lastRow = FIRST_DATA_ROW + formulaRows - 1;
  columns.forEach((c, i) => {
    if (c.role !== ROLE.AUTO) return;
    for (let r = FIRST_DATA_ROW; r <= lastRow; r += 1) {
      const f = formulaFor(c.key, r);
      if (f) ws[`${columnLetter(i)}${r}`] = { t: "s", v: "", f };
    }
  });
  ws["!ref"] = `A${HINT_ROW}:${columnLetter(keys.length - 1)}${Math.max(lastRow, aoa.length)}`;
  ws["!cols"] = [...columns.map((c) => widthFor(c.label, c.key)), ...extraHeaders.map((h) => widthFor(h.label, h.key))];
  ws["!autofilter"] = { ref: `A2:${columnLetter(keys.length - 1)}${lastRow}` };
  return ws;
};

/** A sheet of plain rows, e.g. the READ ME or the Lists sheet. */
export const textSheet = (rows, cols) => {
  const ws = XLSX.utils.aoa_to_sheet(rows);
  if (cols) ws["!cols"] = cols;
  return ws;
};

/**
 * Browser-side download of a built workbook. No-op outside a browser.
 *
 * Shared because the Safari revoke timing below is the kind of detail that gets
 * fixed once and then re-broken in the copy nobody remembered existed.
 */
export const downloadWorkbook = (wb, fileName) => {
  // Compressed: the formula rows make an uncompressed file ten times larger.
  const out = XLSX.write(wb, { bookType: "xlsx", type: "array", compression: true });
  const blob = new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next tick: Safari cancels the download if the URL dies
  // before it has started reading the blob.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

/** "China Town" → "Lusuku_Import_Template_China_Town.xlsx". */
export const templateFileNameFor = (vendorName, fallback) =>
  vendorName
    ? `Lusuku_Import_Template_${String(vendorName).replace(/[^a-z0-9]+/gi, "_").replace(/^_|_$/g, "")}.xlsx`
    : fallback;
